import { Box, Divider, Icon, Typography } from '@mui/material'
import AdxTabsBadge from 'components/reusable/tab/AdxTabsBadge'
import AdxTabsPanel from 'components/reusable/tab/AdxTabsPanel'
import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import {
  getApplicantDetail,
  getJobFitDetail,
} from 'services/api/modules/recruitment/job-application'
import EducationDrawer from './EducationDrawer'
import HeaderDetailsDrawer from './HeaderDetailsDrawer'
import PersonalDrawer from './PersonalDrawer'
import WorkingExperienceDrawer from './WorkingExperienceDrawer'
// import ScoreDrawer from './ScoreDrawer'
import PersonalityDrawer from './PersonalityDrawer'
import JobFitDrawer from './JobFitDrawer'
import ScoreDrawer from './ScoreDrawer'

const TalentDetailDrawer = ({
  id,
  data,
  changeApplicantDetail,
  handleDrawerClose,
  mappedData,
  title,
}) => {
  // API CALL
  const queryClient = useQueryClient()
  useEffect(() => {
    queryClient.invalidateQueries('getApplicantDetail')
    queryClient.resetQueries('getApplicantDetail')
  }, [])

  const {
    isLoading: applicantDetailIsLoading,
    error: applicantDetailError,
    data: applicantDetailData,
  } = useQuery(
    ['getApplicantDetail', id],
    async () => {
      const { data } = await getApplicantDetail(id)
      return data
    },
    {
      refetchOnWindowFocus: false,
    }
  )

  const {
    isLoading: jobFitDetailIsLoading,
    error: jobFitDetailError,
    data: jobFitDetailData,
  } = useQuery(
    ['getJobFitDetail', id],
    async () => {
      const { data } = await getJobFitDetail(id)
      return data
    },
    {
      refetchOnWindowFocus: false,
    }
  )

  // details job fit
  const job_fit = jobFitDetailData

  // details info for applicant
  const details = applicantDetailData

  //applicant skills
  const skills = applicantDetailData?.skills

  //applicant working experience
  const experiences = applicantDetailData?.experiences

  //applicant education background
  const educations = applicantDetailData?.educations

  //applicant education background
  const scores = applicantDetailData?.profile_scores

  // intersection observer
  const useElementOnScreen = (options) => {
    const containerRef = useRef(null)
    const [isVisible, setIsVisible] = useState(false)

    const callbackFunc = (entries) => {
      const [entry] = entries
      setIsVisible(entry.isIntersecting)
    }
    useEffect(() => {
      const observer = new IntersectionObserver(callbackFunc, options)
      if (containerRef.current) observer.observe(containerRef.current)
      return () => {
        if (containerRef.current) observer.unobserve(containerRef.current)
      }
    }, [containerRef, options])
    return [containerRef, isVisible]
  }
  const [containerRef, isVisible] = useElementOnScreen({
    root: null,
    rootMargin: '0px',
    treshold: 1.0,
  })

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        height={'55px'}
        borderBottom={1}
        borderColor="gray-line.main"
        sx={{
          position: 'sticky',
          top: 0,
          width: '100%',
          bgcolor: 'white.main',
          zIndex: 10,
        }}
      >
        <Typography
          px={2}
          variant="body1"
          sx={{
            fontWeight: 'bold',
          }}
        >
          Talent Details
        </Typography>
        <Box display="flex" alignItems="center" columnGap={2}>
          <Icon
            onClick={() => handleDrawerClose('right', false)}
            sx={{
              cursor: 'pointer',
              paddingRight: 4,
              '&:hover': {
                color: 'error.main', // Change the color to red on hover
              },
            }}
          >
            close_icon
          </Icon>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <HeaderDetailsDrawer
          changeApplicantDetail={changeApplicantDetail}
          IntersectionObserverRef={containerRef}
          id={data?.id}
          applicantName={details?.name || data?.id}
          email={details?.email}
          name={details?.name || data?.id}
          contactNumber={details?.contact}
          candidate_id={details?.id}
          data={data}
          job_fit={job_fit}
          date={data?.created_at}
          updated_at={data?.updated_at}
          price={details?.pricing?.unit_price}
          profile_picture={details?.profile_image_url}
          application_status={data?.relations?.job_ad_candidate_status}
          in_cart={details?.pricing?.is_applicant_in_cart}
          paid={data?.relations?.candidateable?.pricing?.is_unlocked}
          loading={applicantDetailIsLoading}
          mappedData={mappedData}
          handleDrawerClose={handleDrawerClose}
          has_video={details?.has_video_interview}
          has_resume={details?.cv_url ? true : false}
          interview_data={data?.relations?.interviews}
          profile_score={details?.profile_scores}
          application_type={data?.relations?.application_type}
          profile_type={details?.profile_type}
        />
        <Box sx={{ overflow: 'auto' }}>
          <AdxTabsBadge
            position="sticky"
            top={0}
            whiteBackground
            zIndex={9}
            initialValue={0}
            scrollButtons="none"
            sxPanel={{ maxHeight: 'calc(100vh - 290px)' }}
          >
            {/* job fit tab */}
            <AdxTabsPanel label="Job Fit Overview">
              <JobFitDrawer
                id={details?.id}
                data={job_fit ?? null}
                isLoading={jobFitDetailIsLoading}
                error={jobFitDetailError}
              />
            </AdxTabsPanel>
            {/* personal information tab */}
            <AdxTabsPanel label="Personal">
              <PersonalDrawer
                profile_type={details?.profile_type}
                name={details?.name || data?.id}
                candidate_id={details?.id}
                skills={skills}
                email={details?.email}
                contactNumber={details?.contact}
                unlocked={data?.relations?.candidateable?.pricing?.is_unlocked}
                has_video={details?.has_video_interview}
                isLoading={applicantDetailIsLoading}
                error={applicantDetailError}
                cv={details?.cv_url}
                videoURL={
                  details?.video_interview_urls !== null
                    ? details?.video_interview_urls[0]?.video_interview_url
                    : null
                }
              />
            </AdxTabsPanel>
            {/* working experience tab  */}
            <AdxTabsPanel label="Working Experience">
              <WorkingExperienceDrawer
                data={experiences ?? null}
                isLoading={applicantDetailIsLoading}
                error={applicantDetailError}
                unlocked={data?.relations?.candidateable?.pricing?.is_unlocked}
              />
            </AdxTabsPanel>

            {/* working education tab */}
            <AdxTabsPanel label="Education">
              <Box>
                <EducationDrawer
                  data={educations ?? null}
                  isLoading={applicantDetailIsLoading}
                  error={applicantDetailError}
                />
              </Box>
            </AdxTabsPanel>

            {/* scoring tab */}
            {/* <AdxTabsPanel label="Score">
              <ScoreDrawer
                data={scores ?? null}
                isLoading={applicantDetailIsLoading}
                error={applicantDetailError}
                profileType={details?.profile_type}
              />
            </AdxTabsPanel> */}

            {/*work personality tab */}
            <AdxTabsPanel label="Work Personality">
              <PersonalityDrawer
                id={details?.id}
                isLoading={applicantDetailIsLoading}
                error={applicantDetailError}
              />
            </AdxTabsPanel>
          </AdxTabsBadge>
        </Box>
      </Box>
    </Box>
  )
}
export default TalentDetailDrawer
